import React, { useState, useEffect } from "react";
import { GlyphButton } from '../Controls/Button/GlyphButton';
import { ReactComponent as ImagePrint } from '../../public/images/svg/ImagePrint.svg';
import { SkitterBookPrintResult } from '../Models/SkitterBookPrintResult';
import { SkitterbookLivePagesRequest, PaperSize, PrinterType } from '../Models/SkitterbookLivePagesRequest';
import { SkitterBookPaperSize } from './SkitterBookPaperSize';
import SkitterBookPrinterType from "./SkitterBookPrinterType";
import { DockPanelHandle } from '../Controls/DockPanel/DockPanel';
import { ScrollDetacher } from '../Controls/ScrollDetacher';
import { authFetch } from "../Utils/FetchUtil";
import './css/SkitterBookLivePrintButton.css';
import { SkitterBookLivePrintPageMargin } from "./SkitterBookLivePrintPageMargin";

interface SkitterBookLivePrintButtonProps {
    skitterBookLivePagesRequest?: SkitterbookLivePagesRequest;
    profileLastChangedOn: number | null;
    dockPanel: React.RefObject<DockPanelHandle | null>;
    onChange?: (newRequest: SkitterbookLivePagesRequest) => void;
    onPageMarginClear?: () => void;
}

type PrintState = "loading" | "building" | "error" | "ready";

export const SkitterBookLivePrintButton: React.FC<SkitterBookLivePrintButtonProps> = ({ skitterBookLivePagesRequest, profileLastChangedOn, dockPanel, onChange, onPageMarginClear }) => {
    const [state, setState] = useState<PrintState>("loading");
    const [pdfUrl, setPdfUrl] = useState<string | undefined>();

    useEffect(() => {
        setState("building");
        onPrintRequestedEventHandler();
    }, [skitterBookLivePagesRequest]);

    const containerclassName =
        `skitterbook-live-print-button skitterbook-live-print-button--${state}`;

    const printButtonContent = <GlyphButton
        ariaLabel="print"
        className={`${containerclassName} component-button`}
        IconComponent={<ImagePrint width={48} height={48} viewBox="0 0 32 32" />}
        label="Print and Assemble"
        labelPosition="left"
        onClick={() => {
            if (pdfUrl && state === "ready") {
                window.open(pdfUrl, "_blank");
            }
        }} />

    return (
        <div className="skitterbook-live-print-button-container settle-effect">

            {skitterBookLivePagesRequest &&
                <div className="skitterbook-print-property-region">
                    <span className="property-region-title">What paper size are you using?</span>
                    <SkitterBookPaperSize value={skitterBookLivePagesRequest.paperSize}
                        onChange={(val: PaperSize) => {
                            const newLivePagesRequest = ({
                                ...skitterBookLivePagesRequest,
                                paperSize: val
                            })

                            onChange?.(newLivePagesRequest);
                        }} />
                </div>
            }

            {skitterBookLivePagesRequest &&
                <div className="skitterbook-print-property-region">
                    <span className="property-region-title">Can your Printer use both sides of the paper?</span>
                    <SkitterBookPrinterType value={skitterBookLivePagesRequest.printerType}
                        onChange={(val: PrinterType) => {
                            const newLivePagesRequest = ({
                                ...skitterBookLivePagesRequest,
                                printerType: val
                            })

                            onChange?.(newLivePagesRequest);
                        }} />
                </div>
            }

            <ScrollDetacher id="print-button-scroll-detacher" alternativeContent={printButtonContent} dockPanel={dockPanel} >
                {printButtonContent}
            </ScrollDetacher>

            {skitterBookLivePagesRequest &&
                <SkitterBookLivePrintPageMargin frontPrintMargin={skitterBookLivePagesRequest.frontPrintMargin}
                    backPrintMargin={skitterBookLivePagesRequest.backPrintMargin}
                    profileLastChangedOn={profileLastChangedOn}
                    onFrontChange={(newPrintMargin) => {
                        const newLivePagesRequest = ({
                            ...skitterBookLivePagesRequest,
                            frontPrintMargin: newPrintMargin
                        })

                        onChange?.(newLivePagesRequest);
                    }}
                    onBackChange={(newPrintMargin) => {
                        const newLivePagesRequest = ({
                            ...skitterBookLivePagesRequest,
                            backPrintMargin: newPrintMargin
                        })

                        onChange?.(newLivePagesRequest);
                    }}
                    onClear={onPageMarginClear}
                />
            }
        </div>
    );

    async function onPrintRequestedEventHandler() {
        try {
            if (skitterBookLivePagesRequest) {
                const uri = `/api/skitterbook/printlivepages?paperSize=${skitterBookLivePagesRequest.paperSize}&printerType=${skitterBookLivePagesRequest.printerType}`;
                const livePagesRequestString = JSON.stringify(skitterBookLivePagesRequest);

                const response = await authFetch(uri, {
                    method: "POST",
                    body: livePagesRequestString
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const data: SkitterBookPrintResult = await response.json();

                if (data) {
                    const byteCharacters = atob(data.documentBytes);
                    const byteNumbers = new Array(byteCharacters.length)
                        .fill(0)
                        .map((_, i) => byteCharacters.charCodeAt(i));
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: 'application/pdf' });
                    const blobUrl = URL.createObjectURL(blob);

                    setPdfUrl(blobUrl);
                    setState("ready");
                }
            }
        } catch (error) {
            setState("error");
        }
    }
};
